import { useEffect, useState } from 'react';
import { useApiHolder } from '@backstage/core-plugin-api';
import { Me, portalAccessApiRef } from './api';

/** api 인스턴스마다 `GET /me` 를 한 번만 읽어 사이드바·화면이 함께 쓴다 */
const cache = new WeakMap<object, Promise<Me>>();
export const resetPortalAccessCache = (api: object) => cache.delete(api);
/** 재시도 신호 — 사이드바·MenuRoute 가 각자 훅을 쓰므로 한 곳의 재시도가 전원을 다시 읽게 한다 */
const retryListeners = new Set<() => void>();

export type PortalAccessState = { loading: boolean; me?: Me; error?: Error; has: (permission: string) => boolean; retry: () => void };

/**
 * 내 권한. api 가 등록돼 있지 않거나 `/me` 가 실패(503 등)하면 권한 없음으로 본다 — UI 힌트일 뿐 실제 차단은 서버(R3 policy).
 */
export function usePortalAccess(): PortalAccessState {
  const api = useApiHolder().get(portalAccessApiRef);
  const [state, setState] = useState<{ loading: boolean; me?: Me; error?: Error }>({ loading: !!api });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!api) {
      setState({ loading: false });
      return undefined;
    }
    let live = true;
    if (!cache.has(api)) cache.set(api, api.me());
    cache.get(api)!.then(
      me => live && setState({ loading: false, me }),
      e => {
        cache.delete(api); // 일시 오류면 다음 마운트에서 다시 시도
        if (live) setState({ loading: false, error: e });
      },
    );
    return () => {
      live = false;
    };
  }, [api, attempt]);
  useEffect(() => {
    const on = () => {
      setState({ loading: true });
      setAttempt(n => n + 1);
    };
    retryListeners.add(on);
    return () => {
      retryListeners.delete(on);
    };
  }, []);
  const retry = () => {
    if (api) cache.delete(api);
    retryListeners.forEach(f => f());
  };
  return { ...state, has: p => !!state.me?.permissions.includes(p), retry };
}
